import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { InlineOption } from "@/Common/Components/Inputs/InlineOptionsControl";
import { BritishColumbiaObstetricalFileClickableTextContext } from "@/Common/Models/UI/ClickableTextContext";
import { BaseBritishColumbiaObstetricalFileSubSection, BaseBritishColumbiaObstetricalFileSubSectionProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/BritishColumbia/Subcomponent/BaseBritishColumbiaObstetricalFileSubSection";
import { InlineRadioOptions } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRendererOptions";
import { BritishColumbiaObstetricalFileTests } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part2/Section13/BritishColumbiaObstetricalFileTests";

class BritishColumbiaObstetricalFileTestsSubSection extends BaseBritishColumbiaObstetricalFileSubSection<BaseBritishColumbiaObstetricalFileSubSectionProps, never> {
    private readonly section13Id = "Section13";
    private readonly rubellaImmuneInternationalUnitsPerMilliliterId = `${this.section13Id}_RubellaImmuneInternationalUnitsPerMilliliter`;
    private readonly humanImmunodeficiencyVirusId = `${this.section13Id}_HumanImmunodeficiencyVirus`;
    private readonly syphyllisId = `${this.section13Id}_Syphyllis`;
    private readonly hepatitisBSurfaceAntigenId = `${this.section13Id}_HepatitisBSurfaceAntigen`;
    private readonly gonorrheaId = `${this.section13Id}_Gonorrhea`;
    private readonly chlamydiaId = `${this.section13Id}_Chlamydia`;
    private readonly urineCultureId = `${this.section13Id}_UrineCulture`;
    private readonly glucoseChallengeTestId = `${this.section13Id}_GlucoseChallengeTest`;
    private readonly glucoseToleranceTestId = `${this.section13Id}_GlucoseToleranceTest`;
    private readonly otherTestsId = `${this.section13Id}_OtherTests`;
    private readonly fieldHeightInPixels = 35;

    private datePicker: DateSelector;

    public render(): JSX.Element {
        const results = this.props.form.Section13.TestResults;

        return (
            <div>
                {this.renderRow(
                    <span className="bold">{this.getString("TestLabel")}</span>,
                    <span className="bold">{this.getString("ResultsLabel")}</span>,
                    <span className="bold">{this.getString("ResultsFollowUpCommentsLabel")}</span>
                )}
                {this.renderRow01(results)}
                {this.renderRow02(results)}
                {this.renderRow03(results)}
                {this.renderRow04(results)}
                {this.renderRow05(results)}
                {this.renderRow06(results)}
                {this.renderRow07(results)}
                {this.renderRow08(results)}
                {this.renderRow09(results)}
                {this.renderRow10(results)}
                {this.renderRow11(results)}
                {this.renderRow12(results)}
            </div>
        );
    }

    private renderRow(
        labelColumn: React.ReactNode,
        resultsColumn: React.ReactNode,
        commentsColumn: React.ReactNode,
        columnWidths: string[] = ["col-md-2", "col-md-2", "col-md-8"],
        renderBorder: boolean = true
    ): JSX.Element {
        const styles: React.CSSProperties = {};
        if (renderBorder)
            styles.borderBottom = "1px solid #e1e1e1";

        return (
            <div style={styles}>
                <div className="row row-form flex-display flex-align-items-center" style={{ margin: "0 -5px" }}>
                    <div className={`${columnWidths[0]} flex-display flex-align-items-center`} style={{ padding: "0 5px" }}>
                        <div className="padding-xs-top padding-xs-bottom padding-sm-left">
                            {labelColumn}
                        </div>
                    </div>
                    <div className={`${columnWidths[1]} flex-display flex-align-items-center`} style={{ padding: "0 5px" }}>
                        <div className="padding-xs-top padding-xs-bottom">
                            {resultsColumn}
                        </div>
                    </div>
                    <div className={`${columnWidths[2]} flex-display flex-align-items-center`} style={{ padding: "0 5px" }}>
                        <div className="padding-xs-top padding-xs-bottom padding-sm-right width-100">
                            {commentsColumn}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderRow01(results: BritishColumbiaObstetricalFileTests): JSX.Element {
        return this.renderRow(
            <span>{this.getString("Rubella")}</span>,

            this.renderInlineRadio({
                value: results?.IsImmuneToRubella,
                onChange: value => this.mutateModelStateThenFocusTextbox(s => s.Section13.TestResults.IsImmuneToRubella = value, value != null, this.rubellaImmuneInternationalUnitsPerMilliliterId),
                items: this.getImmuneOptions(),
                id: "Section13_IsImmuneToRubella",
                optionSpacing: "10px",
                wrapOptions: true
            }),

            <div className="flex-display flex-wrap flex-gap-sm" style={{ justifyContent: "space-between", alignItems: "center" }}>
                <div>
                    {this.renderInlineTextbox(
                        {
                            id: this.rubellaImmuneInternationalUnitsPerMilliliterId,
                            label: null,
                            labelSuffix: this.getLabelWithInternationalUnitsPerMilliliterAbbrevSuffix("ValueLabel"),
                            value: results?.RubellaImmuneInternationalUnitsPerMilliliter?.toString(),
                            onChange: value => this.props.mutateModelState(s => s.Section13.TestResults.RubellaImmuneInternationalUnitsPerMilliliter = value && parseFloat(value)),
                            maxLength: this.page2FieldLimits.Section13_Rubella_TestResults_Value.MaxChars,
                            isNumeric: true,
                            labelClassName: "nowrap"
                        },
                        {
                            width: "75px"
                        }
                    )}
                </div>
                {this.controlRenderer.renderCheckbox(
                    "ObstetricalFile_PostpartumVaccineRequiredLabel",
                    results?.IsPostpartumVaccineRequired,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Section13.TestResults.IsPostpartumVaccineRequired = value),
                    "nowrap",
                    "margin-none"
                )}
            </div>
        );
    }

    private renderRow02(results: BritishColumbiaObstetricalFileTests): JSX.Element {
        return this.renderRow(
            <span>{this.getString("HumanImmunodeficiencyVirusAbbreviationLabel")}</span>,

            this.renderInlineRadio({
                value: results?.IsHumanImmunodeficiencyVirusPositive,
                onChange: value => this.mutateModelStateThenFocusTextbox(s => s.Section13.TestResults.IsHumanImmunodeficiencyVirusPositive = value, value != null, this.humanImmunodeficiencyVirusId),
                items: this.getNegPosOptions(),
                id: "Section13_IsHumanImmunodeficiencyVirusPositive",
                optionSpacing: "10px",
                wrapOptions: true
            }),

            this.renderCheckboxWithTextbox(
                this.humanImmunodeficiencyVirusId,
                "ObstetricalFile_ThirdTrimesterRepeatIfHighRiskLabel",
                results?.ThirdTrimesterRepeatIfHumanImmunodeficiencyVirusIsHighRisk,
                value => this.mutateModelStateThenFocusTextbox(s => s.Section13.TestResults.ThirdTrimesterRepeatIfHumanImmunodeficiencyVirusIsHighRisk = value, value, this.humanImmunodeficiencyVirusId),
                results?.HumanImmunodeficiencyVirusComments,
                value => this.props.mutateModelState(s => s.Section13.TestResults.HumanImmunodeficiencyVirusComments = value),
                this.page2FieldLimits.Section13_HumanImmunodeficiencyVirus_TestResultsComments.MaxChars
            )
        );
    }

    private renderRow03(results: BritishColumbiaObstetricalFileTests): JSX.Element {
        return this.renderRow(
            <span>{this.getString("SyphilisLabel")}</span>,

            this.renderInlineRadio({
                value: results?.IsReactiveToSyphilis,
                onChange: value => this.mutateModelStateThenFocusTextbox(
                    s => s.Section13.TestResults.IsReactiveToSyphilis = value,
                    value != null,
                    this.syphyllisId
                ),
                items: this.getNonReactiveReactiveOptions(),
                id: "Section13_IsReactiveToSyphilis",
                optionSpacing: "10px",
                wrapOptions: true
            }),

            this.getAdditionalTextBox(
                {
                    id: this.syphyllisId,
                    value: results?.SyphilisComments,
                    onChange: value => this.props.mutateModelState(s => s.Section13.TestResults.SyphilisComments = value),
                    maxLength: this.page2FieldLimits.Section13_Syphilis_TestResults_Comments.MaxChars
                }
            )
        );
    }

    private renderRow04(results: BritishColumbiaObstetricalFileTests): JSX.Element {
        return this.renderRow(
            <span>{this.getString("HepatitisBSurfaceAntigenAbbrevLabel")}</span>,

            this.renderInlineRadio({
                value: results?.IsReactiveToHepatitisBSurfaceAntigen,
                onChange: value => this.mutateModelStateThenFocusTextbox(s => s.Section13.TestResults.IsReactiveToHepatitisBSurfaceAntigen = value, value != null, this.hepatitisBSurfaceAntigenId),
                items: this.getNonReactiveReactiveOptions(),
                id: "Section13_IsReactiveToHepatitisBSurfaceAntigen",
                optionSpacing: "10px",
                wrapOptions: true
            }),

            <div className="row resizable">
                <div className="col-md-6 nowrap">
                    {this.getAdditionalTextBox({
                        id: this.hepatitisBSurfaceAntigenId,
                        label: this.getLabelWithInternationalUnitsPerMilliliterAbbrevSuffix("HepatitisBVirusDNAAbbrevLabel"),
                        value: results?.HepatitisBVirusDnaInternationalUnitsPerMilliliter?.toString(),
                        onChange: value => this.props.mutateModelState(s => s.Section13.TestResults.HepatitisBVirusDnaInternationalUnitsPerMilliliter = value && parseFloat(value)),
                        maxLength: this.page2FieldLimits.Section13_HepatitisBSurfaceAntigen_TestResults_DnaValue.MaxChars,
                        valueStyle: { fontWeight: "normal", width: "100%" },
                        isNumeric: true,
                        min: 0
                    })}
                    {this.controlRenderer.renderCheckbox(
                        "PartnerSlashHouseholdContactLabel",
                        results?.HasPartnerOrHouseholdContact,
                        value => this.props.mutateModelStateWithFormUpdate(s => s.Section13.TestResults.HasPartnerOrHouseholdContact = value),
                        "margin-sm-top",
                        "margin-none"
                    )}
                </div>
                <div className="col-md-6">
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_AntiViralTherapyRequiredLabel",
                        results?.IsAntiViralTherapyRequired,
                        value => this.props.mutateModelStateWithFormUpdate(s => s.Section13.TestResults.IsAntiViralTherapyRequired = value)
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_NewbornVaccineRequiredLabel",
                        results?.IsNewbornVaccineRequired,
                        value => this.props.mutateModelStateWithFormUpdate(s => s.Section13.TestResults.IsNewbornVaccineRequired = value)
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_NewbornHepatitisBImmuneGlobulinRequiredAbbrevLabel",
                        results?.IsNewbornHepatitisBImmuneGlobulinRequired,
                        value => this.props.mutateModelStateWithFormUpdate(s => s.Section13.TestResults.IsNewbornHepatitisBImmuneGlobulinRequired = value)
                    )}
                </div>
            </div>
        );
    }

    private renderRow05(results: BritishColumbiaObstetricalFileTests): JSX.Element {
        return this.renderRow(
            <span>{this.getString("GonorrheaLabel")}</span>,

            this.renderInlineRadio({
                value: results?.IsGonorrheaPositive,
                onChange: value => this.mutateModelStateThenFocusTextbox(s => s.Section13.TestResults.IsGonorrheaPositive = value, value != null, this.gonorrheaId),
                items: this.getNegPosOptions(),
                id: "Section13_IsGonorrheaPositive",
                optionSpacing: "10px",
                wrapOptions: true
            }),

            this.renderCheckboxWithTextbox(
                this.gonorrheaId,
                "ObstetricalFile_ThirdTrimesterRepeatIfPositiveAbbrevLabel",
                results?.ThirdTrimesterRepeatIfGonorrheaIsPositive,
                value => this.mutateModelStateThenFocusTextbox(s => s.Section13.TestResults.ThirdTrimesterRepeatIfGonorrheaIsPositive = value, value, this.gonorrheaId),
                results?.GonorrheaComments,
                value => this.props.mutateModelState(s => s.Section13.TestResults.GonorrheaComments = value),
                this.page2FieldLimits.Section13_Gonorrhea_TestResults_Comments.MaxChars
            )
        );
    }

    private renderRow06(results: BritishColumbiaObstetricalFileTests): JSX.Element {
        return this.renderRow(
            <span>{this.getString("ChlamydiaLabel")}</span>,

            this.renderInlineRadio({
                value: results?.IsChlamydiaPositive,
                onChange: value => this.mutateModelStateThenFocusTextbox(s => s.Section13.TestResults.IsChlamydiaPositive = value, value != null, this.chlamydiaId),
                items: this.getNegPosOptions(),
                id: "Section13_IsChlamydiaPositive",
                optionSpacing: "10px",
                wrapOptions: true
            }),

            this.renderCheckboxWithTextbox(
                this.chlamydiaId,
                "ObstetricalFile_ThirdTrimesterRepeatIfPositiveAbbrevLabel",
                results?.ThirdTrimesterRepeatIfChlamydiaIsPositive,
                value => this.mutateModelStateThenFocusTextbox(s => s.Section13.TestResults.ThirdTrimesterRepeatIfChlamydiaIsPositive = value, value, this.chlamydiaId),
                results?.ChlamydiaComments,
                value => this.props.mutateModelState(s => s.Section13.TestResults.ChlamydiaComments = value),
                this.page2FieldLimits.Section13_Chlamydia_TestResults_Comments.MaxChars
            )
        );
    }

    private renderRow07(results: BritishColumbiaObstetricalFileTests): JSX.Element {
        return this.renderRow(
            <span>{this.getString("UrineCSLabel")}</span>,

            this.renderInlineRadio({
                value: results?.IsUrineCultureAndSensitivityPositive,
                onChange: value => this.mutateModelStateThenFocusTextbox(s => s.Section13.TestResults.IsUrineCultureAndSensitivityPositive = value, value != null, this.urineCultureId),
                items: this.getNegPosOptions(),
                id: "Section13_IsUrineCultureAndSensitivityPositive",
                optionSpacing: "10px",
                wrapOptions: true
            }),

            <div className="flex-display flex-wrap flex-col-gap-md flex-row-gap-xs" style={{ alignItems: "center", justifyContent: "stretch" }}>
                <div className="flex-grow-1">
                    {this.renderInlineTextbox({
                        id: this.urineCultureId,
                        label: "CultureLabel",
                        value: results?.UrineCulture,
                        onChange: value => this.props.mutateModelState(s => s.Section13.TestResults.UrineCulture = value),
                        maxLength: this.page2FieldLimits.Section13_UrineCultureAndSensitivity_TestResults_CultureValue.MaxChars,
                        labelStyle: { fontWeight: "normal" }
                    })}
                </div>
                <div className="flex-grow-1">
                    {this.controlRenderer.renderTextbox({
                        value: results?.UrineCultureAndSensitivityComments,
                        onChange: value => this.props.mutateModelState(s => s.Section13.TestResults.UrineCultureAndSensitivityComments = value),
                        maxLength: this.page2FieldLimits.Section13_UrineCultureAndSensitivity_TestResults_Comments.MaxChars
                    })}
                </div>
            </div>
        );
    }

    private renderRow08(results: BritishColumbiaObstetricalFileTests): JSX.Element {
        return this.renderRow(
            <span>{this.getLabelWithExtraInfo("ObstetricalFile_GestationalDiabetesMellitusAbbrevLabel", `(${this.getString("ObstetricalFile_At24To28WeeksAbbrevLabel")})`)}</span>,

            this.controlRenderer.renderCheckbox(
                "ObstetricalFile_GestationalDiabetesMellitusTestDeclinedAbbrevLabel",
                results?.IsGestationalDiabetesMellitusTestDeclined,
                value => this.props.mutateModelStateWithFormUpdate(s => s.Section13.TestResults.IsGestationalDiabetesMellitusTestDeclined = value)
            ),

            <div className="flex-display flex-wrap flex-col-gap-sm">
                {this.controlRenderer.renderCheckboxWithOptions({
                    label: "DietControlledLabel",
                    checked: results?.IsDietControlled,
                    onChange: value => this.props.mutateModelStateWithFormUpdate(s => s.Section13.TestResults.IsDietControlled = value)
                })}
                {this.controlRenderer.renderCheckboxWithOptions({
                    label: "InsulinRequiredLabel",
                    checked: results?.IsInsulinRequired,
                    onChange: value => this.props.mutateModelStateWithFormUpdate(s => s.Section13.TestResults.IsInsulinRequired = value)
                })}
            </div>,

            ["col-md-4", "col-md-3", "col-md-5"],
            false
        );
    }

    private renderRow09(results: BritishColumbiaObstetricalFileTests): JSX.Element {
        return this.renderRow(
            <span>{this.getLabelWithExtraInfo("GlucoseChallengeTestAbbrevLabel", `(50 ${this.getString("GramUnitSymbolLabel")})`)}</span>,

            this.renderInlineRadio({
                value: results?.IsGlucoseChallengeTestPositive,
                onChange: value => this.mutateModelStateThenFocusTextbox(s => s.Section13.TestResults.IsGlucoseChallengeTestPositive = value, value != null, this.glucoseChallengeTestId),
                items: this.getNegPosOptions(),
                id: `${this.section13Id}_IsGlucoseChallengeTestPositive`,
                heightInPixels: this.fieldHeightInPixels,
                optionSpacing: "10px",
                wrapOptions: true
            }),

            <div className="flex-display flex-align-items-center flex-row-gap-sm flex-col-gap-sm">
                <span>{this.getLabelWithExtraInfo("ValueLabel", `(${this.getString("MillimolesPerLitreUnitSymbolLabel")})`)}</span>
                <div className="flex-grow-1" style={{ maxWidth: "120px" }}>
                    {this.renderInlineTextbox(
                        {
                            label: "At1HourAbbrevLabel",
                            value: results?.GlucoseChallengeTestMillimolesPerLiterAt1Hour?.toString(),
                            onChange: value => this.props.mutateModelState(s => s.Section13.TestResults.GlucoseChallengeTestMillimolesPerLiterAt1Hour = value && parseFloat(value)),
                            isNumeric: true,
                            labelClassName: "normal-text nowrap",
                            maxLength: 7
                        }
                    )}
                </div>
            </div>,

            ["col-md-2", "col-md-2", "col-md-8"],
            false
        );
    }

    private renderRow10(results: BritishColumbiaObstetricalFileTests): JSX.Element {
        return this.renderRow(
            <span>{this.getLabelWithExtraInfo("GlucoseToleranceTestAbbrevLabel", `(75 ${this.getString("GramUnitSymbolLabel")})`)}</span>,

            this.renderInlineRadio({
                value: results?.IsGlucoseToleranceTestPositive,
                onChange: value => this.mutateModelStateThenFocusTextbox(s => s.Section13.TestResults.IsGlucoseToleranceTestPositive = value, value != null, this.glucoseToleranceTestId),
                items: this.getNegPosOptions(),
                id: `${this.section13Id}_IsGlucoseToleranceTestPositive`,
                heightInPixels: this.fieldHeightInPixels,
                optionSpacing: "10px",
                wrapOptions: true
            }),

            <div className="flex-display flex-wrap flex-align-items-center flex-row-gap-sm flex-col-gap-sm">
                <span>
                    {this.getLabelWithExtraInfo("ValueLabel", `(${this.getString("MillimolesPerLitreUnitSymbolLabel")})`)}
                </span>
                {this.renderInlineTextbox(
                    {
                        label: "ObstetricalFile_GlycemiaFasting",
                        value: results?.GlucoseToleranceTestMillimolesPerLiterFasting?.toString(),
                        onChange: value => this.props.mutateModelState(s => s.Section13.TestResults.GlucoseToleranceTestMillimolesPerLiterFasting = value && parseFloat(value)),
                        isNumeric: true,
                        labelClassName: "normal-text nowrap",
                        maxLength: 7
                    },
                    {
                        width: "80px"
                    }
                )}
                {this.renderInlineTextbox(
                    {
                        label: "At1HourAbbrevLabel",
                        value: results?.GlucoseToleranceTestMillimolesPerLiterAt1Hour?.toString(),
                        onChange: value => this.props.mutateModelState(s => s.Section13.TestResults.GlucoseToleranceTestMillimolesPerLiterAt1Hour = value && parseFloat(value)),
                        isNumeric: true,
                        labelClassName: "normal-text nowrap",
                        maxLength: 7
                    },
                    {
                        width: "80px"
                    }
                )}
                {this.renderInlineTextbox(
                    {
                        label: "At2HourAbbrevLabel",
                        value: results?.GlucoseToleranceTestMillimolesPerLiterAt2Hour?.toString(),
                        onChange: value => this.props.mutateModelState(s => s.Section13.TestResults.GlucoseToleranceTestMillimolesPerLiterAt2Hour = value && parseFloat(value)),
                        isNumeric: true,
                        labelClassName: "normal-text nowrap",
                        maxLength: 7
                    },
                    {
                        width: "80px"
                    }
                )}
                {!IsNullOrUndefined(results?.GlucoseToleranceTestMillimolesPerLiterAt3Hour) &&
                    this.renderInlineTextbox(
                        {
                            label: "At3HourAbbrevLabel",
                            value: results?.GlucoseToleranceTestMillimolesPerLiterAt3Hour?.toString(),
                            onChange: value => this.props.mutateModelState(s => s.Section13.TestResults.GlucoseToleranceTestMillimolesPerLiterAt3Hour = value && parseFloat(value)),
                            isNumeric: true,
                            labelClassName: "normal-text nowrap",
                            maxLength: 7
                        },
                        {
                            width: "80px"
                        }
                    )
                }
            </div>
        );
    }

    private renderRow11(results: BritishColumbiaObstetricalFileTests): JSX.Element {
        return this.renderRow(
            <span>{this.getLabelWithExtraInfo("ObstetricalFile_GroupBStreptococcusAbbrevLabel", `(${this.getString("ObstetricalFile_At35To37WeeksAbbrevLabel")})`, true)}</span>,

            this.renderInlineRadio({
                value: results?.IsGroupBStreptococcusPositive,
                onChange: value => this.props.mutateModelStateWithFormUpdate(
                    s => s.Section13.TestResults.IsGroupBStreptococcusPositive = value,
                    () => value != null && this.datePicker.open()
                ),
                items: this.getNegPosOptions(),
                id: `${this.section13Id}_IsGlucoseChallengeTestPositive`,
                heightInPixels: this.fieldHeightInPixels,
                optionSpacing: "10px",
                wrapOptions: true
            }),

            <div className="flex-display flex-wrap flex-row-gap-sm flex-col-gap-xs" style={{ alignItems: "center", justifyContent: "space-between" }}>
                {this.renderInlineDatePicker(
                    "DateLabel",
                    results?.GroupBStreptococcusTestDate,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Section13.TestResults.GroupBStreptococcusTestDate = value),
                    "",
                    "normal-text",
                    ref => this.datePicker = ref
                )}
                {this.controlRenderer.renderCheckboxWithOptions({
                    label: "CopyToHospitalLabel",
                    checked: results?.GroupBStreptococcusCopyToHospital,
                    onChange: value => this.props.mutateModelStateWithFormUpdate(s => s.Section13.TestResults.GroupBStreptococcusCopyToHospital = value),
                    labelClassName: "margin-none"
                })}
            </div>
        );
    }

    private renderRow12(results: BritishColumbiaObstetricalFileTests): JSX.Element {
        return (
            <div style={{ borderBottom: "1px solid #e1e1e1" }}>
                <div className="row row-form">
                    <div className="col-md-12 resizable" style={{ position: "relative" }}>
                        <div style={{ padding: "5px 10px 5px 10px" }}>
                            {this.renderField({
                                label: this.props.readonly ? "ObstetricalFile_BC_Tests_OtherWithExamplesLabel" : "OtherLabel",
                                labelClassName: "normal-text",
                                labelSuffix: this.props.readonly ? null : this.getClickableOtherTests(),
                                className: "width-100",
                                field: this.controlRenderer.renderResizableTextarea({
                                    value: results?.OtherTests,
                                    onChange: value => this.props.mutateModelState(s => s.Section13.TestResults.OtherTests = value),
                                    id: this.otherTestsId,
                                    clickableTextOptions: this.props.getClickableTextOptions(this.otherTestsId, BritishColumbiaObstetricalFileClickableTextContext.Section13_OtherTests),
                                    minHeight: "32px",
                                    maxLength: this.page2FieldLimits.Section13_OtherTestResults.MaxChars,
                                    maxRows: this.page2FieldLimits.Section13_OtherTestResults.MaxRows,
                                    className: "textarea-padding"
                                })
                            })}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderInlineRadio<T>(options: InlineRadioOptions<T>): JSX.Element {
        return this.controlRenderer.renderInlineRadio({ ...options, allowDeselect: true, additionalWrapperClassName: "nowrap" });
    }

    private renderCheckboxWithTextbox(
        id: string,
        checkboxLabel: string,
        checkboxValue: boolean,
        onCheckboxChange: (value: boolean) => void,
        texboxValue: string,
        onTextboxChange: (value: string) => void,
        maxLength?: number
    ): JSX.Element {
        return (
            <div className="flex-display nowrap" style={{ alignItems: "center", width: "100%" }}>
                {this.controlRenderer.renderCheckbox(
                    checkboxLabel,
                    checkboxValue,
                    value => onCheckboxChange(value),
                    "margin-none flex-display flex-align-items-center",
                    "margin-none"
                )}
                {this.renderInlineTextbox({
                    id: id,
                    value: texboxValue,
                    onChange: value => onTextboxChange(value),
                    maxLength: maxLength,
                    setInputFullWidth: true,
                    className: "width-100"
                })}
            </div>
        );
    }

    private getLabelWithExtraInfo(label: string, extraInfo: string, wrapDivs: boolean = false): React.ReactNode {
        return (
            <span style={{ display: "flex", flexWrap: wrapDivs ? "wrap" : "nowrap", columnGap: "5px" }}>
                <span className="normal-text">{this.getString(label)}</span>
                {this.getLabelSuffix(extraInfo, "extra-info-label")}
            </span>
        );
    }

    private getLabelWithInternationalUnitsPerMilliliterAbbrevSuffix(label: string): React.ReactNode {
        return this.getLabelWithExtraInfo(label, `(${this.getString("InternationalUnitsPerMilliliterAbbrevLabel")})`);
    }

    private getImmuneOptions(): InlineOption<boolean>[] {
        return [
            new InlineOption<boolean>(true, this.getString("ImmuneAbbrevLabel")),
            new InlineOption<boolean>(false, this.getString("NonImmuneAbbrevLabel"))
        ];
    }

    private getNegPosOptions(): InlineOption<boolean>[] {
        return [
            new InlineOption<boolean>(false, this.getString("NegativeAbbrevLabel")),
            new InlineOption<boolean>(true, this.getString("PositiveAbbrevLabel"))
        ];
    }

    private getNonReactiveReactiveOptions(): InlineOption<boolean>[] {
        return [
            new InlineOption<boolean>(false, this.getString("NonReactiveAbbrevLabel")),
            new InlineOption<boolean>(true, this.getString("ReactiveAbbrevLabel"))
        ];
    }

    private getClickableOtherTests(): React.ReactNode {
        return (
            <span className="margin-xs-left">
                ({this.getClickableOtherTest(this.getString("ThyroidStimulationHormoneAbbreviationLabel"))},&nbsp;
                {this.getString("ObstetricalFile_AdditionalInvestigationsDiabetesScreen")} (
                {this.getClickableOtherTest(this.getString("ObstetricalFile_HemoglobinA1CAbbreviation"))},&nbsp;
                {this.getClickableOtherTest(this.getString("ObstetricalFile_RenalBloodFlowAbbreviation"))},&nbsp;
                {this.getClickableOtherTest(this.getString("ObstetricalFile_ForearmBloodFlowAbbreviation"))}
                ),&nbsp;
                {this.getClickableOtherTest(this.getString("ObstetricalFile_HemoglobinElectrophoresisLabel"))},&nbsp;
                {this.getClickableOtherTest(this.getString("ObstetricalFile_FerritinLabel"))},&nbsp;
                {this.getClickableOtherTest(this.getString("ObstetricalFile_B12Label"))},&nbsp;
                {this.getString("ObstetricalFile_InfectiousDiseasesLabel")} (
                {this.getClickableOtherTest(this.getString("ObstetricalFile_HeppatisCAbbreviation"))},&nbsp;
                {this.getClickableOtherTest(this.getString("ParvoB19Label"))},&nbsp;
                {this.getClickableOtherTest(this.getString("VaricellaLabel"))},&nbsp;
                {this.getClickableOtherTest(this.getString("ToxoLabel"))},&nbsp;
                {this.getClickableOtherTest(this.getString("CytomegalovirusLabelAbbreviation"))}
                ),&nbsp;
                {this.getClickableOtherTest(this.getString("ObstetricalFile_DrugScreenLabel"))},&nbsp;
                {this.getClickableOtherTest(this.getString("ObstetricalFile_RepeatSTIScreenLabel"))},&nbsp;
                {this.getClickableOtherTest(this.getString("Covid19Label"))},&nbsp;
                {this.getClickableOtherTest(this.getString("UrineCSLabel"))})
            </span>
        );
    }

    private getClickableOtherTest(text: string): React.ReactNode {
        return (
            <span
                className={this.props.readonly ? "" : "active-clickable-text"}
                onClick={() => {
                    this.props.mutateModelState(s => {
                        const other = s.Section13.TestResults.OtherTests ?? "";

                        if (!other?.contains(text, { ignoreCase: true, ignoreDiacritics: true })) {
                            s.Section13.TestResults.OtherTests = `${other} ${text}`.trim().substring(0, this.page2FieldLimits.Section13_OtherTestResults.MaxChars);
                        }
                    });
                }}
            >
                {text}
            </span>
        );
    }
}

export { BritishColumbiaObstetricalFileTestsSubSection };
